import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { toast } from "@/utils/toast";

export interface ErrorContext {
  feature?: string;
  action?: string;
  userId?: string;
  workspaceId?: string;
  [key: string]: unknown;
}

export interface ErrorHandlerOptions {
  showToast?: boolean;
  reportToSentry?: boolean;
  context?: ErrorContext;
  severity?: "low" | "medium" | "high" | "critical";
  action?: string;
  toastMessage?: string;
  toastDescription?: string;
}

/**
 * Determines if an error should be reported to Sentry
 */
function shouldReportError(
  error: unknown,
  options?: ErrorHandlerOptions,
): boolean {
  // Don't report if explicitly disabled
  if (options?.reportToSentry === false) {
    return false;
  }

  // Don't report AbortErrors (cancelled requests)
  if (
    error &&
    typeof error === "object" &&
    "name" in error &&
    error.name === "AbortError"
  ) {
    return false;
  }

  // Don't report network timeout errors in dev
  if (
    import.meta.env.DEV &&
    error &&
    typeof error === "object" &&
    "code" in error &&
    error.code === "NETWORK_ERROR"
  ) {
    return false;
  }

  return true;
}

/**
 * Gets user-friendly error message from various error types
 */
function getErrorMessage(error: unknown, t: (key: string) => string): string {
  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return t("errors.http.unexpectedError");
}

/**
 * Unified error handling hook that provides consistent error reporting and user feedback
 */
export function useErrorHandler() {
  const { t } = useTranslation();

  const handleError = useCallback(
    (error: unknown, options: ErrorHandlerOptions = {}) => {
      const {
        showToast = true,
        context = {},
        severity = "medium",
        toastMessage,
        toastDescription,
      } = options;

      const errorMessage = getErrorMessage(error, t);

      // Report to Sentry if appropriate
      if (shouldReportError(error, options)) {
        // Promote a small allowlist of context fields to indexed tags so the
        // issue list groups by failure type/stage instead of collapsing into
        // one generic title. Only string/number values are valid tag values.
        const promotedTags: Record<string, string> = {};
        for (const key of ["errorName", "stage"] as const) {
          const value = context[key];
          if (typeof value === "string" || typeof value === "number") {
            promotedTags[key] = String(value);
          }
        }

        Sentry.captureException(error, {
          tags: {
            feature: context.feature || "unknown",
            action: context.action || options.action || "unknown",
            severity,
            ...promotedTags,
          },
          contexts: {
            error_context: context,
          },
          // Same mapping as the edge error handler: "high" must reach Sentry's
          // high priority (level fatal) or the alert rule never emails.
          level:
            severity === "critical" || severity === "high"
              ? "fatal"
              : severity === "medium"
                ? "error"
                : "info",
        });
      }

      // Show toast notification to user
      if (showToast) {
        const displayMessage = toastMessage || errorMessage;
        const displayDescription = toastDescription;

        // Determine toast type based on error severity
        if (severity === "critical" || severity === "high") {
          toast.error(displayMessage, { description: displayDescription });
        } else {
          toast.warning(displayMessage, { description: displayDescription });
        }
      }

      // Log to console in development
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error("Error handled by useErrorHandler:", error, {
          options,
          context,
        });
      }
    },
    [t],
  );

  /**
   * Handle errors from async operations (promises)
   */
  const handleAsyncError = useCallback(
    async <T>(
      promise: Promise<T>,
      options: ErrorHandlerOptions = {},
    ): Promise<T | null> => {
      try {
        return await promise;
      } catch (error) {
        handleError(error, options);
        return null;
      }
    },
    [handleError],
  );

  /**
   * Create an error handler with predefined context
   */
  const createHandler = useCallback(
    (defaultContext: ErrorContext) => {
      return (error: unknown, options: ErrorHandlerOptions = {}) => {
        handleError(error, {
          ...options,
          context: { ...defaultContext, ...options.context },
        });
      };
    },
    [handleError],
  );

  return useMemo(
    () => ({
      handleError,
      handleAsyncError,
      createHandler,
    }),
    [handleError, handleAsyncError, createHandler],
  );
}
